import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Sala.css";
import exerciciosService from "../services/exercicios.service";
import { useAuth } from "../hooks/useAuth";
import professoresService from "../services/professores.service";
import supabase from "../config/supabaseConfig.js";
import { toast } from "react-toastify";
import AulaAlunosService from "../services/AulaAlunosService";
import { getDataAtual } from "../services/aulas.service";
import { useSala } from "../contexts/SalaContext";
import { getViewAs } from "../lib/viewAs";
import ControleCargaSala from "./ControleCargaSala";

// Seção colapsável (pedido do Rodrigo, 03/07): card do aluno em aula ficou
// longo com o painel de Volume novo — cada bloco (Dados, Último Treino,
// Volume) recolhe com uma seta, reduzindo o scroll.
const SecaoRecolhivel = ({ titulo, className = "", aberta, onToggle, children }) => (
  <div className={`card-container secao-recolhivel ${className}`}>
    <button type="button" className="secao-header" onClick={onToggle}>
      <h4>{titulo}</h4>
      <span className={`secao-seta ${aberta ? "" : "fechada"}`}>▾</span>
    </button>
    {aberta && <div className="secao-corpo">{children}</div>}
  </div>
);

function Sala({ userRole }) {
  const navigate = useNavigate();
  // Dropdown "Selecionar Aluno" REMOVIDO (decisão Rodrigo 14/07): aluno só
  // entra em aula pela Pré-sala — a Sala não monta mais aula manualmente.
  // Regra §3 CORRIGIDA (feedback Tales 10/07): marcar falta é de todos os
  // papéis (professor incluído) — o banco já permite. Só desfazer/justificar
  // ficam restritos a admin/líder (checado inline com userRole abaixo).
  // userRole aqui já é o effectiveRole (respeita "Ver como").
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [salaLoading, setSalaLoading] = useState(true);
  const [error, setError] = useState(null);
  // Seções recolhidas do card do aluno (chave "alunoId:secao"). Default:
  // tudo aberto — recolher é opt-in do professor pra reduzir scroll.
  // Persistido em localStorage (feedback Tales 08/07): ao voltar de Editar
  // cadastro/cargas, a Sala restaura os cards minimizados como estavam.
  const [secoesFechadas, setSecoesFechadas] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("sala:secoesFechadas") || "[]")
      );
    } catch {
      return new Set();
    }
  });
  const secaoAberta = (chave) => !secoesFechadas.has(chave);
  const toggleSecao = (chave) => {
    setSecoesFechadas((prev) => {
      const next = new Set(prev);
      if (next.has(chave)) next.delete(chave);
      else next.add(chave);
      try {
        localStorage.setItem("sala:secoesFechadas", JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  // Usando o contexto SalaContext para acessar os estados persistentes
  const {
    alunoObservacoes,
    exerciciosPorAluno,
    updateAlunoObservacao,
    adicionarExercicio: addExercicio,
    atualizarExercicio: updateExercicio,
    removerExercicio: removeExercicio,
    limparDadosSala,
  } = useSala();

  // Estados do professor
  const [professorAtual, setProfessorAtual] = useState(null);

  // Estados da aula
  const [aulaAtiva, setAulaAtiva] = useState(false);
  const [aulaAtual, setAulaAtual] = useState(null);
  const [alunosEmAula, setAlunosEmAula] = useState([]);
  // Presença/falta por aluno na aula em andamento (aula_alunos.status).
  // Chave = aluno_id; valor = { status, justificativa, justificada_em }.
  const [statusAlunos, setStatusAlunos] = useState({});
  // Modal simples de justificativa de falta (null = fechado).
  const [justificativaModal, setJustificativaModal] = useState(null);
  const [salvandoJustificativa, setSalvandoJustificativa] = useState(false);

  // Estados do aluno
  const [, setTodosExercicios] = useState([]);
  const [ultimosTreinos, setUltimosTreinos] = useState({});
  // Observações do cadastro do aluno (não editáveis)
  const [alunoObservacoesCadastro, setAlunoObservacoesCadastro] = useState({});
  // Mantenha setAlunoObservacoes como uma função que usa updateAlunoObservacao
  const setAlunoObservacoes = (newObservacoes) => {
    // Se receber um objeto, assumimos que é um conjunto de observações
    if (typeof newObservacoes === "object") {
      // Para cada par chave-valor no objeto, atualizar a observação daquele aluno
      Object.entries(newObservacoes).forEach(([alunoId, observacao]) => {
        updateAlunoObservacao(alunoId, observacao);
      });
    }
  };

  // Estados para gerenciar feedback e processamento
  const [, setProcessandoFinalizacao] = useState(false);
  // Aluno sendo finalizado agora — trava o botão contra clique duplo.
  // ⚠️ Havia um `processandoFinalizacao` cujo GETTER foi descartado no
  // useState (`const [, setX]`), então a trava era escrita e nunca lida: o
  // botão ficava clicável durante a gravação e o 2º clique criava um registro
  // idêntico. Casos reais em prod: Maykon Camilo 24/07, Guilherme Francisco
  // 25/07, Paula Simões Carnauba 27/07, Laisa Riechel 28/07 — ~1 por dia.
  const [finalizandoAluno, setFinalizandoAluno] = useState(null);


  // Identificar o professor logado
  useEffect(() => {
    const identificarProfessor = async () => {
      if (authLoading) return;
      setSalaLoading(true);
      setError(null);

      try {
        let profIdParaBuscar = null;

        if (user?.id) {
          profIdParaBuscar = user.id;
        }

        const viewAs = isAdmin ? getViewAs(user?.email) : null;

        if (!profIdParaBuscar) {
          setError("Usuário não identificado.");
          setProfessorAtual(null);
        } else if (
          isAdmin &&
          (viewAs?.role === "professor" || viewAs?.role === "lider") &&
          viewAs.professorId
        ) {
          // Modo coringa "Ver como professor/líder": assume a identidade REAL
          // de um professor cadastrado — aulas.professor_id tem FK p/
          // professores.id e o admin não tem linha lá, então precisa dessa
          // identidade p/ criar aula. Líder com identidade = Alex (dá aula).
          try {
            const professor = await professoresService.getById(
              viewAs.professorId
            );
            setProfessorAtual(professor);
          } catch (err) {
            setError("Professor simulado não encontrado.");
            setProfessorAtual(null);
          }
        } else if (isAdmin) {
          // Admin não precisa de registro em professores — sem query que estoura.
          setProfessorAtual({
            id: user.id,
            nome: user.nome || user.email?.split("@")[0] || "Administrador",
            email: user.email || "",
            role: "admin",
            isAdmin: true,
          });
        } else {
          // Professor: tenta por id; se não achar, por email. Só erra se nada resolver.
          let professor = null;
          try {
            professor = await professoresService.getById(profIdParaBuscar);
          } catch (err) {
            professor = null; // .single() estoura sem linha — segue pro fallback
          }
          if (!professor && user?.email) {
            const todosProfessores = await professoresService.getAll();
            professor =
              todosProfessores.find(
                (prof) =>
                  prof.email?.toLowerCase() === user.email.toLowerCase()
              ) || null;
          }
          if (professor) {
            setProfessorAtual(professor);
          } else {
            setError("Seu usuário não está registrado como professor.");
            setProfessorAtual(null);
          }
        }
      } catch (err) {
        console.error("Erro ao identificar professor:", err);
        setError("Falha ao identificar o professor.");
        setProfessorAtual(null);
      } finally {
        setSalaLoading(false);
      }
    };

    identificarProfessor();
  }, [authLoading, user, isAdmin]);

  // Verificar se o professor tem aula em andamento
  useEffect(() => {
    const verificarAulaEmAndamento = async () => {
      if (!professorAtual) {
        setAulaAtiva(false);
        setAulaAtual(null);
        setAlunosEmAula([]);
        return;
      }

      setSalaLoading(true);
      setError(null);

      try {
        const { data: aulasBasicas, error: aulasBasicasError } = await supabase
          .from("aulas")
          .select("id, status, data, professor_id, observacoes")
          .eq("professor_id", professorAtual.id)
          .eq("status", "em_andamento")
          .order("created_at", { ascending: false });

        if (aulasBasicasError) {
          console.error("Erro ao buscar aulas básicas:", aulasBasicasError);
          throw aulasBasicasError;
        }

        // Sem aula em andamento: NÃO cria mais automaticamente (a aula que
        // "não morria" pós-finalizar era isso — reload recriava na hora e o
        // professor via a aula voltar vazia na Sala e no Telão). A aula nasce
        // da Pré-sala (Liberar) ou do 1º aluno adicionado pelo dropdown.
        if (!aulasBasicas || aulasBasicas.length === 0) {
          setAulaAtual(null);
          setAulaAtiva(false);
          setAlunosEmAula([]);
          setStatusAlunos({});
          setSalaLoading(false);
          return;
        }

        // Usar a aula mais recente (já ordenada por created_at desc)
        const aulaBasica = aulasBasicas[0];

        // Carregar os alunos associados a esta aula
        try {
          const { data: alunosIds, error: alunosIdsError } = await supabase
            .from("aula_alunos")
            .select("aluno_id, status, justificativa, justificada_em")
            .eq("aula_id", aulaBasica.id);

          if (alunosIdsError) {
            console.error(
              "Erro ao buscar IDs dos alunos da aula:",
              alunosIdsError
            );
            throw alunosIdsError;
          }

          let alunosDaAula = [];

          // Se encontrou alunos relacionados, buscar os detalhes completos
          if (alunosIds && alunosIds.length > 0) {
            // Extrair os IDs dos alunos
            const ids = alunosIds.map((record) => record.aluno_id);

            // Buscar os detalhes dos alunos pelo ID
            const { data: alunos, error: alunosError } = await supabase
              .from("alunos")
              .select("*")
              .in("id", ids);

            if (alunosError) {
              console.error("Erro ao buscar detalhes dos alunos:", alunosError);
              throw alunosError;
            }

            alunosDaAula = alunos || [];
          }

          // Montar o objeto completo da aula com os dados que temos
          const aulaCompleta = {
            ...aulaBasica,
            alunos: alunosDaAula,
          };

          // Mapear presença/falta por aluno (aula_alunos.status).
          const mapaStatus = {};
          (alunosIds || []).forEach((r) => {
            mapaStatus[r.aluno_id] = {
              status: r.status || "pendente",
              justificativa: r.justificativa || "",
              justificada_em: r.justificada_em || null,
            };
          });

          // Atualizar os estados com a aula carregada
          setAulaAtual(aulaCompleta);
          setAulaAtiva(true);
          setAlunosEmAula(alunosDaAula);
          setStatusAlunos(mapaStatus);
        } catch (alunosError) {
          console.error("Erro ao buscar alunos da aula:", alunosError);
          // Mesmo com erro nos alunos, ainda carregamos a aula básica
          setAulaAtual(aulaBasica);
          setAulaAtiva(true);
          setAlunosEmAula([]);
        }
      } catch (err) {
        console.error("Erro ao verificar aula em andamento:", err);
        setAulaAtual(null);
        setAulaAtiva(false);
        setAlunosEmAula([]);
      } finally {
        setSalaLoading(false);
      }
    };

    verificarAulaEmAndamento();
  }, [professorAtual]);

  // Buscar último treino para alunos visíveis
  useEffect(() => {
    const buscarUltimosTreinosParaAlunosVisiveis = async () => {
      // Garantir que alunosEmAula é um array
      if (!Array.isArray(alunosEmAula)) {
        console.warn(
          "[WARN] alunosEmAula não é um array, pulando busca de treinos. Valor:",
          alunosEmAula
        );
        setUltimosTreinos({});
        setAlunoObservacoesCadastro({});
        return;
      }

      if (alunosEmAula.length === 0) {
        setUltimosTreinos({});
        setAlunoObservacoesCadastro({});
        return;
      }

      // Criar um novo objeto para armazenar os dados
      const novosUltimosTreinos = {};
      const novasObservacoesCadastro = {}; // Para as observações do cadastro

      // Para cada aluno, buscar separadamente para evitar problemas
      for (const aluno of alunosEmAula) {
        if (!aluno || !aluno.id) {
          console.warn(`[WARN] Aluno inválido encontrado:`, aluno);
          continue;
        }

        // Verificar se já existe uma observação no contexto para este aluno
        if (alunoObservacoes[aluno.id]) {
        }

        try {
          // 1. Buscar os dados cadastrais do aluno diretamente

          const { data: dadosAluno, error: alunoError } = await supabase
            .from("alunos")
            .select("*")
            .eq("id", aluno.id)
            .single();

          if (alunoError) {
            console.error(`[ERROR] Erro ao buscar dados do aluno:`, alunoError);
            continue;
          }

          // Armazenar as observações do cadastro do aluno
          // (estas serão mostradas na seção "Observações do Aluno")
          novasObservacoesCadastro[aluno.id] = dadosAluno.observacoes || "";

          // 2. Buscar último treino finalizado do aluno usando abordagem simplificada

          try {
            // Primeiro buscar todas as aulas em que este aluno participou
            // SÓ aulas em que o aluno esteve PRESENTE (fix 27/07). Sem este
            // filtro o "Último Treino" do card podia ser um dia em que ele
            // FALTOU — casos reais em prod: card mostrava 27/07 pra quem não
            // treinava desde 10/06. O professor lia "treinou ontem" e dosava
            // carga em cima disso.
            const { data: aulasAlunoData, error: aulasAlunoError } =
              await supabase
                .from("aula_alunos")
                .select("aula_id, observacoes")
                .eq("aluno_id", aluno.id)
                .eq("status", "presente");

            if (aulasAlunoError) {
              console.error(
                `[ERROR] Erro ao buscar aulas do aluno:`,
                aulasAlunoError
              );
              continue;
            }

            if (!aulasAlunoData || aulasAlunoData.length === 0) {
              continue;
            }

            // Obter IDs das aulas do aluno
            const aulaIds = aulasAlunoData.map((registro) => registro.aula_id);

            // Mapear observações por ID de aula para uso posterior
            const observacoesPorAula = {};
            aulasAlunoData.forEach((registro) => {
              if (registro.observacoes) {
                observacoesPorAula[registro.aula_id] = registro.observacoes;
              }
            });

            // Buscar as aulas finalizadas
            const { data: aulasFinalizadas, error: aulasFinalizadasError } =
              await supabase
                .from("aulas")
                .select(
                  `
                id, 
                data,
                status,
                observacoes,
                created_at,
                professor:professor_id(id, nome)
              `
                )
                .in("id", aulaIds)
                .eq("status", "finalizada")
                .order("created_at", { ascending: false }) // Ordenar por created_at em vez de data
                .limit(1);

            if (aulasFinalizadasError) {
              console.error(
                `[ERROR] Erro ao buscar aulas finalizadas:`,
                aulasFinalizadasError
              );
              continue;
            }

            if (aulasFinalizadas && aulasFinalizadas.length > 0) {
              // Pegar o primeiro (mais recente) treino
              const ultimoTreino = aulasFinalizadas[0];

              // Adicionar as observações específicas do aluno ao objeto do treino
              if (observacoesPorAula[ultimoTreino.id]) {
                ultimoTreino.observacoes_aluno =
                  observacoesPorAula[ultimoTreino.id];
              }

              // Salvar no map de últimos treinos
              novosUltimosTreinos[aluno.id] = ultimoTreino;
            } else {
            }
          } catch (trenoErr) {
            console.error(`[ERROR] Erro ao buscar último treino:`, trenoErr);
          }
        } catch (err) {
          console.error(
            `[ERROR] Erro processando dados para ${aluno.nome}:`,
            err
          );
        }
      }

      // Atualizar os estados com todos os dados coletados
      setUltimosTreinos(novosUltimosTreinos);
      setAlunoObservacoesCadastro(novasObservacoesCadastro); // Observações do cadastro

      // NÃO inicialize as observações vazias aqui!
      // O Context API já deve ter carregado as observações salvas do localStorage
      // Apenas inicialize observações para novos alunos que não têm observações ainda
    };

    buscarUltimosTreinosParaAlunosVisiveis();
  }, [alunosEmAula, aulaAtual?.id, alunoObservacoes]);

  // Finalizar aula
  const handleFinalizarAula = async () => {
    if (!professorAtual || !professorAtual.id) {
      setError("Professor não identificado. Não é possível finalizar aulas.");
      return;
    }

    try {
      setSalaLoading(true);
      setError(null);

      // Buscar TODAS as aulas em andamento para este professor
      const { data: aulasEmAndamento, error: fetchError } = await supabase
        .from("aulas")
        .select("id")
        .eq("professor_id", professorAtual.id)
        .eq("status", "em_andamento");

      if (fetchError) {
        console.error("ERRO NA BUSCA DE AULAS:", fetchError);
        throw new Error(
          `Erro ao buscar aulas em andamento: ${fetchError.message}`
        );
      }

      if (!aulasEmAndamento || aulasEmAndamento.length === 0) {
        setError("Não há aulas em andamento para finalizar.");
        setSalaLoading(false);
        return;
      }

      // Data LOCAL (BRT, mesma base do `hora` abaixo) — getDataAtual centraliza
      // a regra. UTC datava a aula da noite no dia seguinte (fix 22/07).
      const hoje = new Date();
      const dataFormatadaISO = getDataAtual();

      // Capturar o horário atual para registrar quando a aula foi finalizada
      const horas = String(hoje.getHours()).padStart(2, "0");
      const minutos = String(hoje.getMinutes()).padStart(2, "0");
      const horaAtual = `${horas}:${minutos}`;

      // Para cada aula, realizar a finalização
      for (const aula of aulasEmAndamento) {
        // Antes de limpar os relacionamentos, salvar as observações de cada aluno
        // em aulas finalizadas individuais para preservar o histórico
        try {
          // Buscar alunos e observações desta aula
          const { data: alunosDaAula, error: alunosError } = await supabase
            .from("aula_alunos")
            .select(
              "aluno_id, observacoes, status, justificativa, justificada_por, justificada_em"
            )
            .eq("aula_id", aula.id);

          // Buscar também as observações que estão no state local, não apenas no banco
          // Isso garante que observações não salvas ainda sejam preservadas
          if (!alunosError && alunosDaAula && alunosDaAula.length > 0) {
            // Para cada aluno, criar uma aula finalizada individual
            for (const alunoRegistro of alunosDaAula) {
              const { aluno_id } = alunoRegistro;

              // Buscar observações do state - prioridade para o que está no textarea
              // Se não estiver no state, usar o que foi recuperado do banco
              let observacoes =
                alunoObservacoes[aluno_id] || alunoRegistro.observacoes || "";

              const aulaFinalizadaIndividual = {
                professor_id: professorAtual.id,
                data: dataFormatadaISO, // CORRIGIDO: Usar data calculada
                status: "finalizada",
                observacoes: observacoes, // Usar observações específicas do aluno
                hora: horaAtual, // Adicionar o horário de finalização
              };

              // Inserir a aula finalizada
              const { data: aulaFinalizada, error: aulaError } = await supabase
                .from("aulas")
                .insert(aulaFinalizadaIndividual)
                .select()
                .single();

              if (aulaError) {
                console.error(
                  "Erro ao criar registro de treino finalizado:",
                  aulaError
                );
                throw aulaError;
              }

              // 2. Relacionar o aluno com esta aula finalizada, carimbando a
              // presença. Regra pragmática dos 15min (§3): aula fechada conta
              // como realizada — quem não está com falta vira 'presente' com
              // confirmada_em/consumiu_checkin. Faltas são preservadas (o
              // trigger força consumiu_checkin=false na falta).
              if (aulaFinalizada) {
                const registroPresenca =
                  alunoRegistro.status === "falta"
                    ? {
                        aula_id: aulaFinalizada.id,
                        aluno_id: aluno_id,
                        observacoes: observacoes,
                        status: "falta",
                        justificativa: alunoRegistro.justificativa || null,
                        justificada_por: alunoRegistro.justificada_por || null,
                        justificada_em: alunoRegistro.justificada_em || null,
                      }
                    : {
                        aula_id: aulaFinalizada.id,
                        aluno_id: aluno_id,
                        observacoes: observacoes,
                        status: "presente",
                        confirmada_em: new Date().toISOString(),
                        consumiu_checkin: true,
                      };
                const { error: relError } = await supabase
                  .from("aula_alunos")
                  .insert(registroPresenca);

                if (relError) {
                  console.error(
                    "Erro ao relacionar aluno com aula finalizada:",
                    relError
                  );
                  throw relError;
                }
              }
            }
          }
        } catch (histErr) {
          console.error("Erro ao salvar histórico dos alunos:", histErr);
        }

        // Agora limpar a junction table para eliminar relacionamentos
        const { error: relError } = await supabase
          .from("aula_alunos")
          .delete()
          .eq("aula_id", aula.id);

        if (relError) {
          console.error(`ERRO AO REMOVER RELACIONAMENTOS: ${relError.message}`);
        }

        // Finalizar a aula
        await supabase
          .from("aulas")
          .update({
            status: "finalizada",
            data: dataFormatadaISO, // CORRIGIDO: Usar data calculada
            hora: horaAtual, // Adicionar hora de finalização
            alunos: [],
          })
          .eq("id", aula.id);
      }

      // Limpar os dados do localStorage ao finalizar a aula
      limparDadosSala();

      // Limpar o estado local
      setAulaAtiva(false);
      setAulaAtual(null);
      setAlunosEmAula([]);
      setStatusAlunos({});

      alert("Aula finalizada com sucesso!");

      // Forçar recarga da página após pequeno delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("ERRO AO FINALIZAR AULAS:", err);
      setError(`Falha ao finalizar aulas: ${err.message}`);
    } finally {
      setSalaLoading(false);
    }
  };

  // Função para cancelar a participação do aluno na aula sem salvar treino
  const handleCancelarAlunoIndividual = async (alunoId) => {
    try {
      setProcessandoFinalizacao(true);
      const alunoParaCancelar = alunosEmAula.find(
        (aluno) => aluno.id === alunoId
      );

      if (!alunoParaCancelar) {
        console.error("Aluno não encontrado para cancelar");
        return;
      }

      if (
        window.confirm(
          `Deseja realmente cancelar a participação de ${alunoParaCancelar.nome} na aula? Esta ação não registrará um treino finalizado.`
        )
      ) {
        // Remover aluno da aula atual sem criar registro de aula finalizada
        if (aulaAtual) {
          await AulaAlunosService.removerAluno(aulaAtual.id, alunoId);
        }

        // Atualizar a lista de alunos em aula
        const novosAlunosEmAula = alunosEmAula.filter(
          (aluno) => aluno.id !== alunoId
        );
        setAlunosEmAula(novosAlunosEmAula);

        // Limpar as observações deste aluno
        const novasObservacoes = { ...alunoObservacoes };
        delete novasObservacoes[alunoId];
        setAlunoObservacoes(novasObservacoes);

        toast.success(`${alunoParaCancelar.nome} removido da aula.`);
      }
    } catch (error) {
      console.error("Erro ao cancelar participação do aluno:", error);
      toast.error(`Erro ao cancelar aluno: ${error.message}`);
    } finally {
      setProcessandoFinalizacao(false);
    }
  };


  const handleFinalizarAlunoIndividual = async (alunoId) => {
    // Guarda de reentrada: o disabled do botão cobre o clique duplo humano,
    // esta cobre o caso do evento disparar duas vezes antes do re-render.
    if (finalizandoAluno) return;
    try {
      setFinalizandoAluno(alunoId);
      setProcessandoFinalizacao(true);
      const alunoParaFinalizar = alunosEmAula.find(
        (aluno) => aluno.id === alunoId
      );

      if (!alunoParaFinalizar) {
        console.error("Aluno não encontrado para finalizar");
        return;
      }

      // Salvar observações do aluno
      const observacoesAluno = alunoObservacoes[alunoId] || "";

      if (aulaAtual) {
        // Data LOCAL (BRT, mesma base do `hora`) via getDataAtual — antes era
        // UTC e datava a aula da noite no dia seguinte (fix 22/07).
        const hoje = new Date();
        const dataFormatadaISO = getDataAtual();

        // Capturar horário atual para registrar o momento da finalização
        const horas = String(hoje.getHours()).padStart(2, "0");
        const minutos = String(hoje.getMinutes()).padStart(2, "0");
        const horaAtual = `${horas}:${minutos}`;

        const aulaIndividualFinalizada = {
          professor_id: aulaAtual.professor_id,
          data: dataFormatadaISO, // Usar data UTC calculada
          status: "finalizada",
          observacoes: observacoesAluno, // Usar observações específicas do aluno
          hora: horaAtual, // Registrar o horário em que o aluno foi finalizado
        };

        // Inserir a aula finalizada
        const { data: aulaFinalizada, error: aulaError } = await supabase
          .from("aulas")
          .insert(aulaIndividualFinalizada)
          .select()
          .single();

        if (aulaError) {
          console.error(
            "Erro ao criar registro de treino finalizado:",
            aulaError
          );
          throw aulaError;
        }

        // 2. Relacionar o aluno com esta aula finalizada, carimbando presença.
        // Se estava marcado como falta, preserva a falta; senão vira presente
        // (regra pragmática dos 15min §3 — ver handleFinalizarAula).
        if (aulaFinalizada) {
          const statusAtual = statusAlunos[alunoId];
          const registroPresenca =
            statusAtual?.status === "falta"
              ? {
                  aula_id: aulaFinalizada.id,
                  aluno_id: alunoId,
                  observacoes: observacoesAluno,
                  status: "falta",
                  justificativa: statusAtual.justificativa || null,
                }
              : {
                  aula_id: aulaFinalizada.id,
                  aluno_id: alunoId,
                  observacoes: observacoesAluno,
                  status: "presente",
                  confirmada_em: new Date().toISOString(),
                  consumiu_checkin: true,
                };
          const { error: relError } = await supabase
            .from("aula_alunos")
            .insert(registroPresenca);

          if (relError) {
            console.error(
              "Erro ao relacionar aluno com aula finalizada:",
              relError
            );
            throw relError;
          }

          // 3. Salvar os exercícios selecionados para este aluno
          const exerciciosDoAluno = exerciciosPorAluno[alunoId] || [];
          if (exerciciosDoAluno.length > 0) {
            // Criar registros na tabela aula_exercicios para cada exercício
            for (const exercicio of exerciciosDoAluno) {
              if (exercicio.exercicio_id) {
                try {
                  const { error: exError } = await supabase
                    .from("aula_exercicios")
                    .insert({
                      aula_id: aulaFinalizada.id,
                      exercicio_id: exercicio.exercicio_id,
                      aluno_id: alunoId,
                    });

                  if (exError) {
                    console.error(
                      `Erro ao salvar exercício ${exercicio.exercicio_id}:`,
                      exError
                    );
                  } else {
                  }
                } catch (exErr) {
                  console.error("Erro ao inserir exercício:", exErr);
                }
              }
            }
          }
        }
      }

      // 4. Remover aluno da aula atual
      if (aulaAtual) {
        await AulaAlunosService.removerAluno(aulaAtual.id, alunoId);
      }

      // Atualizar a lista de alunos em aula
      const novosAlunosEmAula = alunosEmAula.filter(
        (aluno) => aluno.id !== alunoId
      );
      setAlunosEmAula(novosAlunosEmAula);

      // Limpar as observações deste aluno
      const novasObservacoes = { ...alunoObservacoes };
      delete novasObservacoes[alunoId];
      setAlunoObservacoes(novasObservacoes);

      toast.success(`${alunoParaFinalizar.nome} finalizado com sucesso!`);
    } catch (error) {
      console.error("Erro ao finalizar aluno:", error);
      toast.error(`Erro ao finalizar aluno: ${error.message}`);
    } finally {
      setProcessandoFinalizacao(false);
      setFinalizandoAluno(null);
    }
  };

  // Função para determinar a classe de CSS com base na gravidade da lesão
  const getLesaoClass = (lesao) => {
    switch (lesao) {
      case "Sim - Lesao Grave":
        return "lesao-grave";
      case "Sim - Lesao Moderada":
        return "lesao-moderada";
      default:
        return "";
    }
  };

  // Carregar todos os exercícios disponíveis
  useEffect(() => {
    const carregarExercicios = async () => {
      try {
        const exercicios = await exerciciosService.getAll();
        setTodosExercicios(exercicios);
      } catch (err) {
        console.error("Erro ao carregar exercícios:", err);
      }
    };

    carregarExercicios();
  }, []);

  // Navegações via react-router (respeitam o basename — window.location
  // com caminho absoluto quebrava o deploy em subcaminho do homolog).
  const navegarParaHistoricoAluno = (alunoId) => {
    // ?from=sala: o Detalhe mostra "Voltar para Sala" (fluxo de aula).
    navigate(`/detalhe-aluno/${alunoId}?from=sala`);
  };

  // Atalho pedido em reunião (12/06): editar o cadastro do aluno direto da
  // aula, sem passar por Alunos → buscar → detalhes → editar. Caso de uso:
  // aluno relata uma dor em aula, registrar na hora sem perder a informação.
  const navegarParaEditarAluno = (alunoId) => {
    // ?from=sala: EditarAluno mostra botão "Voltar para Sala" e volta pra
    // cá (em vez do detalhe do aluno) ao salvar/cancelar.
    navigate(`/editar-aluno/${alunoId}?from=sala`);
  };

  // Full screen do aluno (aluno.html): visão de leitura em aula, com
  // carrossel entre os alunos desta aula (?aula= passa o id pra ele
  // buscar os "irmãos" em aula_alunos).
  const abrirFullScreen = (alunoId) => {
    const aula = aulaAtual?.id ? `&aula=${aulaAtual.id}` : "";
    navigate(`/aluno/${alunoId}?from=sala${aula}`);
  };

  // Falta (regra §3 corrigida 10/07): marcar falta é liberado pra todos os
  // papéis. Desfazer/justificar continuam gateados (["admin","lider"]) no
  // render abaixo — o trigger de banco também aplica essa restrição.

  // Marca falta: aula_alunos.status = 'falta' (o trigger de banco força
  // consumiu_checkin = false e a auditoria registra a mudança).
  const marcarFalta = async (aluno) => {
    if (!aulaAtual?.id) return;
    try {
      const { error: updErr } = await supabase
        .from("aula_alunos")
        .update({ status: "falta" })
        .eq("aula_id", aulaAtual.id)
        .eq("aluno_id", aluno.id);
      if (updErr) throw updErr;
      setStatusAlunos((prev) => ({
        ...prev,
        [aluno.id]: {
          status: "falta",
          justificativa: prev[aluno.id]?.justificativa || "",
          justificada_em: prev[aluno.id]?.justificada_em || null,
        },
      }));
      toast.success(`Falta de ${aluno.nome} registrada.`);
    } catch (err) {
      console.error("Erro ao registrar falta:", err);
      toast.error(`Não foi possível registrar a falta: ${err.message}`);
    }
  };

  // Desfazer falta: volta o aluno para 'pendente' (presença só é confirmada
  // no fechamento da aula). Limpa a justificativa associada.
  const desfazerFalta = async (aluno) => {
    if (!aulaAtual?.id) return;
    try {
      const { error: updErr } = await supabase
        .from("aula_alunos")
        .update({
          status: "pendente",
          justificativa: null,
          justificada_por: null,
          justificada_em: null,
        })
        .eq("aula_id", aulaAtual.id)
        .eq("aluno_id", aluno.id);
      if (updErr) throw updErr;
      setStatusAlunos((prev) => ({
        ...prev,
        [aluno.id]: {
          status: "pendente",
          justificativa: "",
          justificada_em: null,
        },
      }));
      toast.success(`Falta de ${aluno.nome} desfeita.`);
    } catch (err) {
      console.error("Erro ao desfazer falta:", err);
      toast.error(`Não foi possível desfazer a falta: ${err.message}`);
    }
  };

  const abrirJustificativa = (aluno) => {
    setJustificativaModal({
      alunoId: aluno.id,
      nome: aluno.nome,
      texto: statusAlunos[aluno.id]?.justificativa || "",
    });
  };

  // Salva/edita justificativa da falta. O prazo (até o fim do dia da aula,
  // exceto admin) é validado pelo trigger de banco; se estourar, o Postgres
  // levanta check_violation e mostramos uma mensagem amigável.
  const salvarJustificativa = async () => {
    if (!justificativaModal || !aulaAtual?.id) return;
    const { alunoId, nome, texto } = justificativaModal;
    const textoLimpo = texto.trim();
    if (!textoLimpo) {
      toast.info("Escreva a justificativa antes de salvar.");
      return;
    }
    try {
      setSalvandoJustificativa(true);
      const { error: updErr } = await supabase
        .from("aula_alunos")
        .update({
          justificativa: textoLimpo,
          justificada_por: user?.id || null,
          justificada_em: new Date().toISOString(),
        })
        .eq("aula_id", aulaAtual.id)
        .eq("aluno_id", alunoId);
      if (updErr) throw updErr;
      setStatusAlunos((prev) => ({
        ...prev,
        [alunoId]: {
          status: prev[alunoId]?.status || "falta",
          justificativa: textoLimpo,
          justificada_em: new Date().toISOString(),
        },
      }));
      setJustificativaModal(null);
      toast.success(`Justificativa de ${nome} registrada.`);
    } catch (err) {
      console.error("Erro ao salvar justificativa:", err);
      // Trigger de prazo -> ERRCODE check_violation (23514).
      const prazoExpirado =
        err.code === "23514" ||
        /prazo de justificativa/i.test(err.message || "");
      toast.error(
        prazoExpirado
          ? "Prazo de justificativa expirado: a falta só pode ser justificada até o fim do dia da aula."
          : `Não foi possível salvar a justificativa: ${err.message}`
      );
    } finally {
      setSalvandoJustificativa(false);
    }
  };

  return (
    <div className="sala">
      <h1>
        Sala de Aula - {professorAtual ? professorAtual.nome : "Carregando..."}
      </h1>
      <div className="data-atual">
        {new Date().toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })}
      </div>

      {/* Área de mensagens de erro e carregamento */}
      {(salaLoading || authLoading) && (
        <div className="loading-container">Carregando dados...</div>
      )}
      {error && <div className="error-message">{error}</div>}

      {/* Mostrar a interface principal apenas depois de carregar */}
      {!salaLoading && !authLoading && professorAtual && (
        <div className="sala-container">
          {/* Sem aula em andamento (pós-finalizar ou início do dia) */}
          {!aulaAtiva && (
            <div className="sala-sem-aula">
              <p>
                <b>Nenhuma aula em andamento.</b>
              </p>
              <p className="sala-sem-aula-hint">
                Os alunos chegam pela Pré-sala — a aula abre na liberação da
                recepção.
              </p>
            </div>
          )}

          {/* Lista de todos os alunos em aula com seus detalhes sempre visíveis */}
          {aulaAtiva && alunosEmAula.length > 0 && (
            <div className="detalhes-alunos-container">
              <h2>Alunos em Aula</h2>
              <div className="detalhes-aluno-grid">
                {alunosEmAula.map((aluno) => (
                  <div key={aluno.id} className="aluno-card-completo">
                    <div className="card-header">
                      <div className="aluno-nome-container">
                        {/* Nome clicável abre a tela cheia (feedback Tales
                            07/07 — substituiu o botão "Abrir"). */}
                        <h3
                          className="aluno-nome-clicavel"
                          onClick={() => abrirFullScreen(aluno.id)}
                          title="Abrir aluno em tela cheia"
                        >
                          {aluno.nome}
                        </h3>
                        <div className="aluno-nome-acoes">
                          <button
                            onClick={() => navegarParaEditarAluno(aluno.id)}
                            className="btn-historico-mini btn-editar-mini-sala"
                            title="Editar cadastro do aluno"
                          >
                            ✎ Editar
                          </button>
                          <button
                            onClick={() => navegarParaHistoricoAluno(aluno.id)}
                            className="btn-historico-mini"
                            title="Ver histórico de aulas"
                          >
                            Histórico
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {/* Painel de dados do aluno */}
                      <div className="painel-dados-aluno">
                        <SecaoRecolhivel
                          titulo="Dados do Aluno"
                          className={getLesaoClass(aluno.lesao)}
                          aberta={secaoAberta(`${aluno.id}:dados`)}
                          onToggle={() => toggleSecao(`${aluno.id}:dados`)}
                        >
                          <div className="dados-aluno">
                            <p>
                              <strong>Nome:</strong> {aluno.nome}
                            </p>
                            <p>
                              <strong>Idade:</strong>{" "}
                              {aluno.idade || "Não informado"}
                            </p>
                            <p>
                              <strong>Lesão:</strong>{" "}
                              {aluno.tipo_lesao || "Não"}
                            </p>
                            <p>
                              <strong>Plano:</strong>{" "}
                              {aluno.plano || "Não informado"}
                            </p>
                            <p>
                              <strong>Nível:</strong>{" "}
                              {aluno.nivel || "Iniciante"}
                            </p>
                          </div>

                          {/* Observações cadastradas */}
                          <div className="observacoes-aluno">
                            <h4>Observações do Aluno:</h4>
                            <div className="observacoes-texto">
                              {alunoObservacoesCadastro[aluno.id] ||
                                "Sem observações cadastradas."}
                            </div>
                          </div>

                          {/* Exercícios obrigatórios (pedido Rodrigo 17/07):
                              no 1º card, abaixo das observações, SEMPRE
                              visível (estado vazio igual ao full screen).
                              Referência de carga ficou SÓ no full screen —
                              entrou aqui por engano e saiu no mesmo dia. */}
                          <div className="exercicios-aluno">
                            <h4>Exercícios Obrigatórios:</h4>
                            <div
                              className={`exercicios-obrigatorios-texto ${
                                aluno.exercicios_obrigatorios ? "" : "vazio"
                              }`}
                            >
                              {aluno.exercicios_obrigatorios ||
                                "Nenhum exercício obrigatório registrado."}
                            </div>
                          </div>
                        </SecaoRecolhivel>
                      </div>

                      {/* Último treino do aluno */}
                      <div className="painel-ultimo-treino">
                        <SecaoRecolhivel
                          titulo="Último Treino"
                          aberta={secaoAberta(`${aluno.id}:treino`)}
                          onToggle={() => toggleSecao(`${aluno.id}:treino`)}
                        >
                          <div className="ultimo-treino-content">
                            {ultimosTreinos[aluno.id] ? (
                              <div className="ultimo-treino-info">
                                <p>
                                  <strong>Data:</strong>{" "}
                                  {ultimosTreinos[aluno.id].data
                                    ? new Date(
                                        ultimosTreinos[aluno.id].data +
                                          "T00:00:00"
                                      ).toLocaleDateString("pt-BR")
                                    : "Data não disponível"}
                                </p>
                                <p>
                                  <strong>Professor:</strong>{" "}
                                  {ultimosTreinos[aluno.id].professor?.nome ||
                                    "Não informado"}
                                </p>
                                {ultimosTreinos[aluno.id].observacoes && (
                                  <div className="ultimo-treino-observacoes">
                                    <p className="observacoes-titulo">
                                      <strong>Observações:</strong>
                                    </p>
                                    <div className="observacoes-conteudo">
                                      {ultimosTreinos[aluno.id].observacoes}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="sem-treino">
                                Este aluno não possui treinos anteriores
                              </p>
                            )}
                          </div>
                        </SecaoRecolhivel>
                      </div>

                      {/* Controle de carga (02/10): SUBSTITUI o painel de
                          Volume por Grupamento no card — pedido Rodrigo/Tales.
                          Mesma tabela do Editar Cargas, editável aqui. */}
                      <div className="painel-volume-grupamento">
                        <SecaoRecolhivel
                          titulo="Controle de carga"
                          aberta={secaoAberta(`${aluno.id}:volume`)}
                          onToggle={() => toggleSecao(`${aluno.id}:volume`)}
                        >
                          <ControleCargaSala alunoId={aluno.id} />
                        </SecaoRecolhivel>
                      </div>

                      {/* Adicionar observação / Falta registrada (feedback
                          Tales 10/07): o box de observação agora abriga o
                          Faltou (ghost vermelho) no lugar do Cancelar e o
                          Cancelar no lugar do Finalizar Aluno; Finalizar
                          Aluno saiu do box e virou botão destacado logo
                          abaixo. Quando o aluno está com falta, o box mostra
                          o estado "Falta registrada" no lugar do textarea —
                          Justificar/Desfazer só pra admin/líder (regra §3
                          corrigida; marcar falta é de todos os papéis). */}
                      <div className="painel-adicionar-observacao">
                        <div className="card-container">
                          {statusAlunos[aluno.id]?.status === "falta" ? (
                            <div className="card-footer-faltou faltou-registrada">
                              <span className="falta-marcada-label">
                                Falta registrada
                              </span>
                              {["admin", "lider"].includes(userRole) && (
                                <>
                                  <button
                                    onClick={() => abrirJustificativa(aluno)}
                                    className="btn-justificar-falta"
                                    title="Registrar/editar justificativa da falta"
                                  >
                                    {statusAlunos[aluno.id]?.justificativa
                                      ? "Editar justificativa"
                                      : "Justificar"}
                                  </button>
                                  <button
                                    onClick={() => desfazerFalta(aluno)}
                                    className="btn-desfazer-falta"
                                    title="Desfazer a falta (volta para pendente)"
                                  >
                                    Desfazer
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <>
                              <h4>Adicionar Observação</h4>
                              <div className="adicionar-observacao-content">
                                {(() => {
                                  // Log para depurar o valor passado para o textarea
                                  const observacaoAtual =
                                    alunoObservacoes[aluno.id] || "";
                                  return (
                                    <textarea
                                      className="observacao-textarea"
                                      value={observacaoAtual}
                                      onChange={(e) => {
                                        // Usar a função do contexto para atualizar a observação do aluno
                                        // e persistir no localStorage
                                        updateAlunoObservacao(
                                          aluno.id,
                                          e.target.value
                                        );
                                      }}
                                      placeholder={`Adicione observações para ${aluno.nome} ...`}
                                      rows={5}
                                    ></textarea>
                                  );
                                })()}
                                <div className="observacao-actions">
                                  <button
                                    className="btn-faltou-card"
                                    onClick={() => marcarFalta(aluno)}
                                    title="Registrar falta"
                                  >
                                    Faltou
                                  </button>
                                  <button
                                    className="btn-cancelar-aluno"
                                    onClick={() =>
                                      handleCancelarAlunoIndividual(aluno.id)
                                    }
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          className="btn-finalizar-aluno btn-finalizar-aluno-destaque"
                          disabled={finalizandoAluno !== null}
                          onClick={() =>
                            handleFinalizarAlunoIndividual(aluno.id)
                          }
                        >
                          {finalizandoAluno === aluno.id
                            ? "Finalizando…"
                            : "Finalizar Aluno"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botão para finalizar aula na parte inferior */}
          {aulaAtiva && (
            <div className="finalizar-aula-container">
              <button
                className="btn-finalizar-aula"
                onClick={handleFinalizarAula}
                disabled={salaLoading}
              >
                Finalizar Sala
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal simples de justificativa de falta (só admin/líder/recepção
          chegam aqui pelo gate do card). O prazo é validado no trigger. */}
      {justificativaModal && (
        <div
          className="justificativa-overlay"
          onClick={() => !salvandoJustificativa && setJustificativaModal(null)}
        >
          <div
            className="justificativa-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Justificar falta — {justificativaModal.nome}</h3>
            <textarea
              className="justificativa-textarea"
              rows={5}
              value={justificativaModal.texto}
              onChange={(e) =>
                setJustificativaModal((prev) => ({
                  ...prev,
                  texto: e.target.value,
                }))
              }
              placeholder="Descreva o motivo da falta..."
            />
            <div className="justificativa-actions">
              <button
                className="btn-cancelar-aluno"
                onClick={() => setJustificativaModal(null)}
                disabled={salvandoJustificativa}
              >
                Cancelar
              </button>
              <button
                className="btn-finalizar-aluno"
                onClick={salvarJustificativa}
                disabled={salvandoJustificativa}
              >
                {salvandoJustificativa ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sala;
